import {
  atlassianGatewayBaseUrl,
  atlassianGatewayPattern,
  atlassianServiceAccountApiTokenSteps,
  atlassianServiceAccountOAuth,
  atlassianServiceAccountOAuthSteps,
} from "../atlassianServiceAccount";
import { basicAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const keywords =
  "atlassian wiki docs documentation knowledge base intranet pages spaces";

export const confluence = defineProduct({
  name: "Confluence",
  tagLine: "Team wiki and documentation",
  logo,
  tags: ["featured", "productivity", "documents"],
  variants: [
    productVariant({
      identifier: "confluence",
      authLabel: "API token without scopes",
      keywords,
      protocol: "http",
      defaultUrlPattern: () =>
        "https://[^/]*\\.atlassian\\.net/wiki/rest/api/.*",
      auth: (app) =>
        basicAuth({
          username: app.config.email,
          password: app.secret.apiToken,
        }),
      steps: [
        {
          title: "Create an API token",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in as the Atlassian user whose permissions the connector should use, then open [API tokens](https://id.atlassian.com/manage-profile/security/api-tokens). This connection type uses the user’s email address and an API token without scopes. For a service account connection, choose **Service account API token** or **Service account OAuth 2.0**. Give the user only the Confluence permissions needed for the connector’s requests.\n\nClick **Create API token**, then name the token and choose an expiration date.",
            },
          ],
        },
        {
          title: "Copy and paste the API token here",
          items: [
            {
              kind: "input",
              label: "API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
            },
          ],
        },
        {
          title: "Enter the account’s email address",
          items: [
            {
              kind: "input",
              label: "API token account email",
              placeholder: "you@example.com",
              required: true,
              target: { kind: "configuration", field: "email" },
            },
          ],
        },
        {
          title: "Enter your Confluence account domain",
          items: [
            {
              kind: "hostname",
              label: "Confluence account domain",
              placeholder: "example.atlassian.net",
              required: true,
              target: { kind: "configuration", field: "domain" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ domain }) => `https://${domain}/wiki/rest/api/user/current`,
      },
      publicEnvVars: ({ domain, email }) => ({
        CONFLUENCE_URL: domain ? `https://${domain}` : "",
        CONFLUENCE_EMAIL: email || "",
      }),
    }),
    productVariant({
      identifier: "confluenceServiceAccountApiToken",
      authLabel: "Service account API token",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ cloudId }) =>
        atlassianGatewayPattern("confluence", cloudId),
      auth: (app) =>
        basicAuth({
          username: app.config.email,
          password: app.secret.apiToken,
        }),
      steps: atlassianServiceAccountApiTokenSteps(
        "Confluence",
        "read:confluence-user"
      ),
      connectionTest: {
        protocol: "http",
        request: ({ cloudId }) =>
          `${atlassianGatewayBaseUrl("confluence", cloudId || "cloud-id")}/wiki/rest/api/user/current`,
      },
      publicEnvVars: ({ cloudId }) => ({
        CONFLUENCE_URL: atlassianGatewayBaseUrl("confluence", cloudId),
      }),
      usageHint: () =>
        "Use process.env.CONFLUENCE_URL as the Atlassian gateway base URL for Confluence API requests.",
    }),
    productVariant({
      identifier: "confluenceServiceAccountOAuth",
      authLabel: "Service account OAuth 2.0",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ cloudId }) =>
        atlassianGatewayPattern("confluence", cloudId),
      auth: oauthAuth,
      steps: atlassianServiceAccountOAuthSteps(
        "Confluence",
        "read:confluence-user"
      ),
      oauth: atlassianServiceAccountOAuth,
      connectionTest: {
        protocol: "http",
        request: ({ cloudId }) =>
          `${atlassianGatewayBaseUrl("confluence", cloudId || "cloud-id")}/wiki/rest/api/user/current`,
      },
      publicEnvVars: ({ cloudId }) => ({
        CONFLUENCE_URL: atlassianGatewayBaseUrl("confluence", cloudId),
      }),
      usageHint: () =>
        "Use process.env.CONFLUENCE_URL as the Atlassian gateway base URL for Confluence API requests.",
    }),
  ],
});
